import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { authFetch } from '@/utils/auth-fetch';
import { AlertCircle, Palette, Plus, X } from 'lucide-react';

interface SegmentEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  segment?: any;
  siteId: string;
  onSave: () => void;
}

// Predefined color palette
const COLOR_PALETTE = [
  { name: 'Green', value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F59E0B' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Gray', value: '#6B7280' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Amber', value: '#F97316' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Cyan', value: '#06B6D4' },
];

interface Criteria {
  metric: string;
  operator: string;
  value: string;
  unit?: string;
}

const CRITERIA_TEMPLATES = {
  engagement: [
    { metric: 'Visits (30 days)', operators: ['≥', '>', '<', '≤', '='], unit: 'visits' },
    { metric: 'Avg Session Duration', operators: ['>', '<', '≥', '≤'], unit: 'min' },
    { metric: 'Pages per Session', operators: ['≥', '>', '<', '≤'], unit: 'pages' },
    { metric: 'Bounce Rate', operators: ['<', '≤', '>', '≥'], unit: '%' },
    { metric: 'Return Visitor', operators: ['is', 'is not'], unit: '' },
  ],
  behavior: [
    { metric: 'Purchase Frequency', operators: ['Daily', 'Weekly', 'Bi-weekly', 'Monthly', 'Rarely'], unit: '' },
    { metric: 'Cart Abandonment', operators: ['<', '≤', '>', '≥'], unit: '%' },
    { metric: 'Discount Usage', operators: ['High', 'Medium', 'Low', 'None'], unit: '' },
    { metric: 'Product Views', operators: ['≥', '>', '<', '≤'], unit: 'products' },
    { metric: 'Time on Site', operators: ['>', '<', '≥', '≤'], unit: 'min' },
  ],
  demographics: [
    { metric: 'Location', operators: ['is', 'is not', 'contains'], unit: '' },
    { metric: 'Device Type', operators: ['Desktop', 'Mobile', 'Tablet'], unit: '' },
    { metric: 'Browser', operators: ['Chrome', 'Safari', 'Firefox', 'Edge', 'Other'], unit: '' },
    { metric: 'Language', operators: ['is', 'is not'], unit: '' },
  ],
};

export const SegmentEditModal: React.FC<SegmentEditModalProps> = ({
  isOpen,
  onClose,
  segment,
  siteId,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    segment_name: '',
    description: '',
    color: '#6B7280',
    typical_behavior: '',
    key_interests: '',
    pain_points: '',
    recommendations: '',
  });
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (segment) {
      setFormData({
        segment_name: segment.segment_name || '',
        description: segment.description || '',
        color: segment.color || '#6B7280',
        typical_behavior: segment.characteristics?.typical_behavior || '',
        key_interests: segment.characteristics?.key_interests || '',
        pain_points: segment.characteristics?.pain_points || '',
        recommendations: segment.marketing_guidelines?.recommendations?.join('\n') || '',
      });
      // Load existing criteria if available
      if (segment.characteristics?.criteria) {
        setCriteria(segment.characteristics.criteria);
      }
    } else {
      // Reset for new segment
      setFormData({
        segment_name: '',
        description: '',
        color: '#6B7280',
        typical_behavior: '',
        key_interests: '',
        pain_points: '',
        recommendations: '',
      });
      setCriteria([]);
    }
  }, [segment]);

  const addCriteria = (category: string) => {
    const template = CRITERIA_TEMPLATES[category as keyof typeof CRITERIA_TEMPLATES][0];
    const newCriteria: Criteria = {
      metric: template.metric,
      operator: Array.isArray(template.operators) ? template.operators[0] : template.operators,
      value: '',
      unit: template.unit,
    };
    setCriteria([...criteria, newCriteria]);
  };

  const updateCriteria = (index: number, field: keyof Criteria, value: string) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: value };
    
    // Update operator options when metric changes
    if (field === 'metric') {
      const allTemplates = [...CRITERIA_TEMPLATES.engagement, ...CRITERIA_TEMPLATES.behavior, ...CRITERIA_TEMPLATES.demographics];
      const template = allTemplates.find(t => t.metric === value);
      if (template) {
        updated[index].operator = Array.isArray(template.operators) ? template.operators[0] : template.operators;
        updated[index].unit = template.unit;
      }
    }
    
    setCriteria(updated);
  };

  const removeCriteria = (index: number) => {
    setCriteria(criteria.filter((_, i) => i !== index));
  };

  const getOperatorsForMetric = (metric: string) => {
    const allTemplates = [...CRITERIA_TEMPLATES.engagement, ...CRITERIA_TEMPLATES.behavior, ...CRITERIA_TEMPLATES.demographics];
    const template = allTemplates.find(t => t.metric === metric);
    return template?.operators || ['='];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        segment_name: formData.segment_name,
        description: formData.description,
        color: formData.color,
        characteristics: {
          typical_behavior: formData.typical_behavior,
          key_interests: formData.key_interests,
          pain_points: formData.pain_points,
          criteria: criteria.filter(c => c.value), // Only include criteria with values
        },
        marketing_guidelines: {
          recommendations: formData.recommendations.split('\n').filter(r => r.trim()),
        },
      };

      const url = segment
        ? `/segment-management/${siteId}/${segment.id}`
        : `/segment-management/${siteId}`;
      
      const method = segment ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save segment');
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save segment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {segment ? 'Edit Segment' : 'Create New Segment'}
            </DialogTitle>
            <DialogDescription>
              {segment
                ? 'Update the segment details and criteria.'
                : 'Define a new customer segment with specific criteria.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="criteria">Segment Criteria</TabsTrigger>
              <TabsTrigger value="marketing">Marketing</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="name">Segment Name *</Label>
                <Input
                  id="name"
                  value={formData.segment_name}
                  onChange={(e) =>
                    setFormData({ ...formData, segment_name: e.target.value })
                  }
                  placeholder="e.g., Premium Shoppers"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of this segment"
                  rows={2}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="color">Segment Color</Label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-gray-300"
                    style={{ backgroundColor: formData.color }}
                  />
                  <div className="grid grid-cols-6 gap-2 flex-1">
                    {COLOR_PALETTE.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        className={`w-8 h-8 rounded border-2 transition-all ${
                          formData.color === color.value
                            ? 'border-gray-900 scale-110'
                            : 'border-gray-300 hover:border-gray-500'
                        }`}
                        style={{ backgroundColor: color.value }}
                        onClick={() => setFormData({ ...formData, color: color.value })}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="behavior">Typical Behavior</Label>
                <Textarea
                  id="behavior"
                  value={formData.typical_behavior}
                  onChange={(e) =>
                    setFormData({ ...formData, typical_behavior: e.target.value })
                  }
                  placeholder="Describe typical behavior patterns"
                  rows={2}
                />
              </div>
            </TabsContent>

            <TabsContent value="criteria" className="space-y-4">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Segment Criteria</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addCriteria('engagement')}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Engagement
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addCriteria('behavior')}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Behavior
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addCriteria('demographics')}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Demographics
                    </Button>
                  </div>
                </div>

                {criteria.length === 0 ? (
                  <div className="text-center py-8 border rounded-lg">
                    <p className="text-muted-foreground">
                      No criteria defined. Click the buttons above to add criteria.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {criteria.map((criterion, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs">Metric</Label>
                          <Select
                            value={criterion.metric}
                            onValueChange={(value) => updateCriteria(index, 'metric', value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Visits (30 days)">Visits (30 days)</SelectItem>
                              <SelectItem value="Avg Session Duration">Avg Session Duration</SelectItem>
                              <SelectItem value="Pages per Session">Pages per Session</SelectItem>
                              <SelectItem value="Bounce Rate">Bounce Rate</SelectItem>
                              <SelectItem value="Return Visitor">Return Visitor</SelectItem>
                              <SelectItem value="Purchase Frequency">Purchase Frequency</SelectItem>
                              <SelectItem value="Cart Abandonment">Cart Abandonment</SelectItem>
                              <SelectItem value="Discount Usage">Discount Usage</SelectItem>
                              <SelectItem value="Product Views">Product Views</SelectItem>
                              <SelectItem value="Time on Site">Time on Site</SelectItem>
                              <SelectItem value="Device Type">Device Type</SelectItem>
                              <SelectItem value="Location">Location</SelectItem>
                              <SelectItem value="Browser">Browser</SelectItem>
                              <SelectItem value="Language">Language</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="w-24">
                          <Label className="text-xs">Operator</Label>
                          <Select
                            value={criterion.operator}
                            onValueChange={(value) => updateCriteria(index, 'operator', value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getOperatorsForMetric(criterion.metric).map((op) => (
                                <SelectItem key={op} value={op}>
                                  {op}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="w-24">
                          <Label className="text-xs">Value</Label>
                          <div className="flex items-center gap-1">
                            {criterion.metric === 'Return Visitor' || 
                             criterion.operator === 'is' || 
                             criterion.operator === 'is not' ? (
                              <Select
                                value={criterion.value}
                                onValueChange={(value) => updateCriteria(index, 'value', value)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="true">Yes</SelectItem>
                                  <SelectItem value="false">No</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                type="text"
                                value={criterion.value}
                                onChange={(e) => updateCriteria(index, 'value', e.target.value)}
                                placeholder="Value"
                                className="h-9"
                              />
                            )}
                            {criterion.unit && (
                              <span className="text-xs text-muted-foreground">
                                {criterion.unit}
                              </span>
                            )}
                          </div>
                        </div>

                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeCriteria(index)}
                          className="h-9 px-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="marketing" className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="interests">Key Interests</Label>
                <Input
                  id="interests"
                  value={formData.key_interests}
                  onChange={(e) =>
                    setFormData({ ...formData, key_interests: e.target.value })
                  }
                  placeholder="e.g., Quality, Innovation, Exclusivity"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="pain_points">Pain Points</Label>
                <Input
                  id="pain_points"
                  value={formData.pain_points}
                  onChange={(e) =>
                    setFormData({ ...formData, pain_points: e.target.value })
                  }
                  placeholder="e.g., Price sensitivity, Time constraints"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="recommendations">
                  Marketing Recommendations (one per line)
                </Label>
                <Textarea
                  id="recommendations"
                  value={formData.recommendations}
                  onChange={(e) =>
                    setFormData({ ...formData, recommendations: e.target.value })
                  }
                  placeholder="Enter recommendations, one per line"
                  rows={4}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : segment ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};